import { useEffect } from "react";
import "@/App.css";
import "@/styles/a11y-asl.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "@/contexts/AuthContext";
import { I18nProvider } from "@/lib/I18nContext";
import { UiOverlayProvider } from "@/contexts/UiOverlayContext";
import { AhaCelebrationProvider } from "@/contexts/AhaCelebrationContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";
import Landing from "@/pages/Landing";
import Inventory from "@/components/Inventory";
import Radar from "@/components/Radar";
import Chat from "@/pages/Chat";
import Share from "@/pages/Share";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import AdminDashboard from "@/pages/AdminDashboard";
import Market from "@/pages/Market";
import { logEvent } from "@/lib/telemetry";
import { RoboBallExpressionProvider } from "@/components/figutour/RoboBallExpressionContext";
import { FiguTourProvider } from "@/components/figutour/FiguTourProvider";
import { Toaster } from "@/components/ui/sonner";
import AslFeedbackBorder from "@/components/AslFeedbackBorder";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  useEffect(() => {
    // Force dark variant on root for shadcn components
    document.documentElement.classList.add("dark");
    document.body.classList.add("bg-black", "text-white");
  }, []);

  // Phase 14 — silent fire-and-forget telemetry: session_start (once per
  // tab) and pwa_installed (window `appinstalled` event).
  useEffect(() => {
    logEvent("session_start");
  }, []);

  useEffect(() => {
    const handler = () => logEvent("pwa_installed");
    window.addEventListener("appinstalled", handler);
    return () => window.removeEventListener("appinstalled", handler);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
       <I18nProvider>
       <UiOverlayProvider>
        <AhaCelebrationProvider>
         <BrowserRouter>
          <RoboBallExpressionProvider>
          <FiguTourProvider>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Landing />} />
            <Route path="/signup" element={<Landing />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <AppShell />
                </ProtectedRoute>
              }
            >
              <Route index element={<Inventory />} />
              <Route path="radar" element={<Radar />} />
              <Route path="chat" element={<Chat />} />
              <Route path="chat/:threadId" element={<Chat />} />
              <Route path="share" element={<Share />} />
              {/* Phase 22a — MatchMarket + MatchBid placeholder */}
              <Route path="market" element={<Market />} />
              {/* Phase 19c/d — Admin dashboard nested under AppShell. */}
              <Route path="admin" element={<AdminDashboard />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster
            position="top-right"
            theme="dark"
            duration={2000}
            style={{ zIndex: "var(--z-toast)" }}
            toastOptions={{
              style: {
                background: "#0A0A0A",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "#fff",
              },
            }}
          />
          <AslFeedbackBorder />
          </FiguTourProvider>
          </RoboBallExpressionProvider>
         </BrowserRouter>
        </AhaCelebrationProvider>
       </UiOverlayProvider>
       </I18nProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
